import { useMutation } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";

export interface UseGenerateReportInput {
	format: "pdf" | "csv";
	kind: "event" | "loan" | "batch";
	target: string;
	dateFrom: string | null;
	dateTo: string | null;
}

export interface UseGenerateReportReturn {
	generate: (input: UseGenerateReportInput) => Promise<void>;
	isPending: boolean;
	isError: boolean;
	error: Error | null;
	reset: () => void;
}

/**
 * Mutation that generates and downloads a report in either PDF or CSV format.
 *
 * PDF: calls POST /api/public/reports/certificate/download (PDF stream).
 * CSV: calls POST /api/public/reports/data.csv (CSV stream).
 *
 * Both endpoints stream binary content, so we bypass the typed ts-rest client
 * and call fetch directly — following the same pattern as `useGenerateCertificate`.
 */
export function useGenerateReport(apiBaseUrl: string): UseGenerateReportReturn {
	// Enforce provider requirement — result unused since binary endpoints
	// bypass the typed client.
	useApiClient();

	const mutation = useMutation({
		mutationFn: async (input: UseGenerateReportInput): Promise<void> => {
			const { format, kind, target, dateFrom, dateTo } = input;

			const endpoint =
				format === "pdf"
					? `${apiBaseUrl}/api/public/reports/certificate/download`
					: `${apiBaseUrl}/api/public/reports/data.csv`;

			const body =
				format === "pdf"
					? JSON.stringify({
							kind,
							target,
							includeProof: true,
							dateFrom: dateFrom ?? undefined,
							dateTo: dateTo ?? undefined,
						})
					: JSON.stringify({
							kind,
							target,
							dateFrom: dateFrom ?? undefined,
							dateTo: dateTo ?? undefined,
						});

			const response = await fetch(endpoint, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body,
			});

			if (!response.ok) {
				throw new Error(
					`Report generation failed: ${response.status} ${response.statusText}`,
				);
			}

			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			const ext = format === "pdf" ? "pdf" : "csv";
			const safeDateFrom = dateFrom ?? "all";
			const filename = `homesite-${kind}-${target.slice(0, 10)}-${safeDateFrom}.${ext}`;

			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(url), 10_000);
		},
	});

	return {
		generate: (input) => mutation.mutateAsync(input),
		isPending: mutation.isPending,
		isError: mutation.isError,
		error: mutation.error,
		reset: mutation.reset,
	};
}
